import React, { useState, useRef, useEffect } from 'react';
import {
    CButton,
    CCard,
    CCardBody,
    CCardTitle,
    CCol,
    CForm,
    CFormInput,
    CFormLabel,
    CFormCheck,
    CRow,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
    CFormSelect,
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CModalFooter,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPlus, cilTrash, cilCamera } from '@coreui/icons';
import CookieFunctions from '../functions/CookieFunctions';
import { toast } from 'react-toastify';

export default function MaintenanceForm() {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    const [vehicleType, setVehicleType] = useState(null); // null, 'truck', or 'trailer'
    const [hasMaintenanceDone, setHasMaintenanceDone] = useState(null); // null, true, or false
    const [truckNumber, setTruckNumber] = useState('');
    const [trailerNumbers, setTrailerNumbers] = useState([]);
    const [selectedTrailerNumber, setSelectedTrailerNumber] = useState('');
    const [month, setMonth] = useState(currentMonth.toString());
    const [year, setYear] = useState(currentYear.toString());
    
    const [maintenanceRecords, setMaintenanceRecords] = useState([
        { date: '', type: '', typeOther: '', mileage: '', vendor: '', invoiceNumber: '' }
    ]);

    const [greased, setGreased] = useState(false); // Default to false (No)
    const [oilChange, setOilChange] = useState(false); // Default to false (No)
    const [confirmAccuracy, setConfirmAccuracy] = useState(false);
    
    const [invoiceFiles, setInvoiceFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState([]);
    
    // Camera states
    const [showCamera, setShowCamera] = useState(false);
    const [stream, setStream] = useState(null);
    const [capturedImages, setCapturedImages] = useState([]);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);

    const months = [
        { value: '1', label: 'January' },
        { value: '2', label: 'February' },
        { value: '3', label: 'March' },
        { value: '4', label: 'April' },
        { value: '5', label: 'May' },
        { value: '6', label: 'June' },
        { value: '7', label: 'July' },
        { value: '8', label: 'August' },
        { value: '9', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' }
    ];

    const maintenanceTypes = [
        'Oil Change',
        'Tire Rotation',
        'Brake Inspection',
        'Engine Repair',
        'Transmission Service',
        'Battery Replacement',
        'Air Filter Replacement',
        'Coolant Flush',
        'Fuel System Service',
        'Suspension Repair',
        'Electrical Repair',
        'Other'
    ];

    // Auto-populate truck number from cookie on component mount
    useEffect(() => {
        const truckNum = CookieFunctions.getTruckNumber();
        if (truckNum) {
            setTruckNumber(truckNum);
        }
    }, []);

    // Fetch trailer numbers when vehicle type is set to 'trailer'
    useEffect(() => {
        if (vehicleType === 'trailer') {
            const fetchTrailerNumbers = async () => {
                try {
                    const myHeaders = new Headers();
                    myHeaders.append('Authorization', 'Bearer ' + CookieFunctions.getJWTToken());

                    const response = await fetch('/api/drivers/trailer/getTrailerNumbers', {
                        method: 'GET',
                        headers: myHeaders
                    });

                    if (response.ok) {
                        const data = await response.json();
                        setTrailerNumbers(data);

                        // Auto-select if only one trailer
                        if (data.length === 1) {
                            setSelectedTrailerNumber(data[0]);
                        } else if (data.length === 0) {
                            toast.info('No trailers found associated with your account.', {
                                position: toast.POSITION.BOTTOM_CENTER,
                                theme: "colored",
                            });
                        }
                    } else {
                        throw new Error('Failed to fetch trailer numbers');
                    }
                } catch (error) {
                    toast.error(error.message || 'Error fetching trailer numbers', {
                        position: toast.POSITION.BOTTOM_CENTER,
                        theme: "colored",
                    });
                }
            };

            fetchTrailerNumbers();
        } else {
            // Reset trailer selection when switching back to truck
            setTrailerNumbers([]);
            setSelectedTrailerNumber('');
        }
    }, [vehicleType]);

    const addMaintenanceRecord = () => {
        setMaintenanceRecords([
            ...maintenanceRecords,
            { date: '', type: '', typeOther: '', mileage: '', vendor: '', invoiceNumber: '' }
        ]);
    };

    const removeMaintenanceRecord = (index) => {
        if (maintenanceRecords.length > 1) {
            const updatedRecords = maintenanceRecords.filter((_, i) => i !== index);
            setMaintenanceRecords(updatedRecords);
        }
    };

    const updateMaintenanceRecord = (index, field, value) => {
        const updatedRecords = [...maintenanceRecords];
        updatedRecords[index][field] = value;
        
        // Clear typeOther if type is not 'Other'
        if (field === 'type' && value !== 'Other') {
            updatedRecords[index]['typeOther'] = '';
        }
        
        setMaintenanceRecords(updatedRecords);
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const maxSize = 20 * 1024 * 1024; // 20 MB
        
        const validFiles = files.filter(file => {
            if (file.size > maxSize) {
                toast.error(`${file.name} exceeds 20 MB limit`, {
                    position: toast.POSITION.BOTTOM_CENTER,
                    theme: "colored",
                });
                return false;
            }
            return true;
        });

        setInvoiceFiles([...invoiceFiles, ...validFiles]);
    };

    const removeFile = (index) => {
        const updatedFiles = invoiceFiles.filter((_, i) => i !== index);
        setInvoiceFiles(updatedFiles);
    };

    // Camera functions
    const startCamera = async () => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: 'environment',
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                }
            });
            
            setStream(mediaStream);
            setShowCamera(true);
            
            // Wait for modal to render, then set video source
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                }
            }, 100);
        } catch (error) {
            toast.error('Unable to access camera. Please check permissions.', {
                position: toast.POSITION.BOTTOM_CENTER,
                theme: "colored",
            });
            console.error('Camera error:', error);
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        
        // Clean up preview URLs
        capturedImages.forEach(img => {
            if (img.preview) {
                URL.revokeObjectURL(img.preview);
            }
        });
        
        setShowCamera(false);
        setCapturedImages([]);
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            
            const context = canvas.getContext('2d');
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            canvas.toBlob((blob) => {
                if (blob) {
                    const timestamp = new Date().getTime();
                    const file = new File([blob], `invoice_${timestamp}.jpg`, {
                        type: 'image/jpeg',
                        lastModified: timestamp
                    });
                    
                    const previewUrl = URL.createObjectURL(blob);
                    
                    setCapturedImages([...capturedImages, {
                        file: file,
                        preview: previewUrl
                    }]);
                    
                    toast.success('Photo captured!', {
                        position: toast.POSITION.BOTTOM_CENTER,
                        theme: "colored",
                        autoClose: 1000
                    });
                }
            }, 'image/jpeg', 0.9);
        }
    };

    const removeCapturedImage = (index) => {
        const imageToRemove = capturedImages[index];
        if (imageToRemove && imageToRemove.preview) {
            URL.revokeObjectURL(imageToRemove.preview);
        }
        const updatedImages = capturedImages.filter((_, i) => i !== index);
        setCapturedImages(updatedImages);
    };

    const savePhotos = () => {
        const photos = capturedImages.map(img => img.file);
        setInvoiceFiles([...invoiceFiles, ...photos]);
        
        toast.success(`${photos.length} photo(s) added to invoices`, {
            position: toast.POSITION.BOTTOM_CENTER,
            theme: "colored",
        });
        
        stopCamera();
    };

    const validateForm = () => {
        const validationErrors = [];

        if (!vehicleType) {
            validationErrors.push('Please select vehicle type (Truck or Trailer)');
        }

        if (hasMaintenanceDone === null) {
            validationErrors.push(`Please indicate if you have completed monthly ${vehicleType || 'vehicle'} maintenance`);
        }

        if (vehicleType === 'truck') {
            if (!truckNumber.trim()) {
                validationErrors.push('Truck number is required');
            }
        } else if (vehicleType === 'trailer') {
            if (!selectedTrailerNumber) {
                validationErrors.push('Trailer number is required');
            }
        }

        if (!month || !year) {
            validationErrors.push('Month and year are required');
        }

        if (hasMaintenanceDone === true) {
            const hasValidRecord = maintenanceRecords.some(record => 
                record.date || record.type || record.mileage || record.vendor || record.invoiceNumber
            );

            if (!hasValidRecord) {
                validationErrors.push('At least one maintenance record must be filled');
            }

            maintenanceRecords.forEach((record, index) => {
                if (record.date || record.type || record.mileage || record.vendor || record.invoiceNumber) {
                    if (!record.date) validationErrors.push(`Record ${index + 1}: Date is required`);
                    if (!record.type) validationErrors.push(`Record ${index + 1}: Type is required`);
                    if (record.type === 'Other' && !record.typeOther) {
                        validationErrors.push(`Record ${index + 1}: Please specify the type of maintenance`);
                    }
                    if (!record.mileage) validationErrors.push(`Record ${index + 1}: Mileage is required`);
                    if (!record.vendor) validationErrors.push(`Record ${index + 1}: Vendor is required`);
                }
            });
        }

        if (!confirmAccuracy) {
            validationErrors.push('You must confirm that all information is correct');
        }

        setErrors(validationErrors);
        return validationErrors.length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const myHeaders = new Headers();
            myHeaders.append('Authorization', 'Bearer ' + CookieFunctions.getJWTToken());

            const formData = new FormData();
            formData.append('vehicleType', vehicleType);
            formData.append('truckNumber', vehicleType === 'truck' ? truckNumber : selectedTrailerNumber);
            formData.append('month', month);
            formData.append('year', year);
            formData.append('hasMaintenanceDone', hasMaintenanceDone);

            // Process maintenance records to include typeOther in type field if "Other" is selected
            const processedRecords = maintenanceRecords.map(record => ({
                ...record,
                type: record.type === 'Other' ? record.typeOther : record.type
            }));

            formData.append('maintenanceRecords', JSON.stringify(processedRecords));
            formData.append('greased', greased);
            formData.append('oilChange', oilChange);

            invoiceFiles.forEach((file) => {
                formData.append('invoiceFiles', file);
            });

            const response = await fetch('/api/drivers/maintenance/submit', {
                method: 'POST',
                headers: myHeaders,
                body: formData
            });

            if (response.ok) {
                toast.success('Maintenance form submitted successfully!', {
                    position: toast.POSITION.BOTTOM_CENTER,
                    theme: "colored",
                });

                // Re-populate truck number after reset
                const identifier = CookieFunctions.getTruckNumber() || '';

                setVehicleType(null);
                setHasMaintenanceDone(null);
                setTruckNumber(identifier);
                setSelectedTrailerNumber('');
                setTrailerNumbers([]);
                setMonth(currentMonth.toString());
                setYear(currentYear.toString());
                setMaintenanceRecords([{ date: '', type: '', typeOther: '', mileage: '', vendor: '', invoiceNumber: '' }]);
                setGreased(false);
                setOilChange(false);
                setConfirmAccuracy(false);
                setInvoiceFiles([]);
                setErrors([]);

                // Reset file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            } else {
                const errorText = await response.text();
                throw new Error(errorText || 'Failed to submit form');
            }
        } catch (error) {
            toast.error(error.message || 'Error submitting form', {
                position: toast.POSITION.BOTTOM_CENTER,
                theme: "colored",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <CCard>
                <CCardBody>
                    <CCardTitle className='fs-2'>Vehicle Maintenance Form</CCardTitle>
                    <hr />

                    <CForm onSubmit={handleSubmit}>
                        {/* Vehicle Type Selection */}
                        <CRow className='mb-4'>
                            <CCol>
                                <CFormLabel className='fw-bold fs-5'>
                                    Are you submitting maintenance for a Truck or Trailer? *
                                </CFormLabel>
                                <div className='d-flex gap-4 mt-2'>
                                    <CFormCheck
                                        type='radio'
                                        name='vehicleType'
                                        id='vehicleTypeTruck'
                                        label='Truck'
                                        checked={vehicleType === 'truck'}
                                        onChange={() => setVehicleType('truck')}
                                    />
                                    <CFormCheck
                                        type='radio'
                                        name='vehicleType'
                                        id='vehicleTypeTrailer'
                                        label='Trailer'
                                        checked={vehicleType === 'trailer'}
                                        onChange={() => setVehicleType('trailer')}
                                    />
                                </div>
                            </CCol>
                        </CRow>

                        {/* Initial Question */}
                        {vehicleType && (
                            <CRow className='mb-4'>
                                <CCol>
                                    <CFormLabel className='fw-bold fs-5'>
                                        Have you had any maintenance performed on your {vehicleType}? *
                                    </CFormLabel>
                                    <div className='d-flex gap-4 mt-2'>
                                        <CFormCheck
                                            type='radio'
                                            name='maintenanceCompleted'
                                            id='maintenanceYes'
                                            label='Yes'
                                            checked={hasMaintenanceDone === true}
                                            onChange={() => setHasMaintenanceDone(true)}
                                        />
                                        <CFormCheck
                                            type='radio'
                                            name='maintenanceCompleted'
                                            id='maintenanceNo'
                                            label='No'
                                            checked={hasMaintenanceDone === false}
                                            onChange={() => setHasMaintenanceDone(false)}
                                        />
                                    </div>
                                </CCol>
                            </CRow>
                        )}

                        {hasMaintenanceDone !== null && (
                            <>
                                <CRow className='mb-3'>
                                    <CCol md={4}>
                                        {vehicleType === 'truck' ? (
                                            <>
                                                <CFormLabel htmlFor='truckNumber'>Truck Number *</CFormLabel>
                                                <CFormInput
                                                    id='truckNumber'
                                                    value={truckNumber}
                                                    onChange={(e) => setTruckNumber(e.target.value)}
                                                    placeholder='Enter truck number'
                                                    readOnly
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <CFormLabel htmlFor='trailerNumber'>Trailer Number *</CFormLabel>
                                                {trailerNumbers.length > 0 ? (
                                                    <CFormSelect
                                                        id='trailerNumber'
                                                        value={selectedTrailerNumber}
                                                        onChange={(e) => setSelectedTrailerNumber(e.target.value)}
                                                    >
                                                        <option value=''>Select a trailer...</option>
                                                        {trailerNumbers.map(trailer => (
                                                            <option key={trailer} value={trailer}>{trailer}</option>
                                                        ))}
                                                    </CFormSelect>
                                                ) : (
                                                    <CFormInput
                                                        id='trailerNumber'
                                                        value=''
                                                        placeholder='No trailers available'
                                                        disabled
                                                    />
                                                )}
                                            </>
                                        )}
                                    </CCol>
                                    <CCol md={4}>
                                        <CFormLabel htmlFor='month'>Month *</CFormLabel>
                                        <CFormSelect
                                            id='month'
                                            value={month}
                                            onChange={(e) => setMonth(e.target.value)}
                                        >
                                            {months.map(m => (
                                                <option key={m.value} value={m.value}>{m.label}</option>
                                            ))}
                                        </CFormSelect>
                                    </CCol>
                                    <CCol md={4}>
                                        <CFormLabel htmlFor='year'>Year *</CFormLabel>
                                        <CFormInput
                                            type='number'
                                            id='year'
                                            value={year}
                                            onChange={(e) => setYear(e.target.value)}
                                            min={currentYear - 5}
                                            max={currentYear}
                                        />
                                    </CCol>
                                </CRow>

                                {hasMaintenanceDone === true && (
                                    <>
                                        <CRow className='mb-3'>
                                            <CCol>
                                                <div className='d-flex justify-content-between align-items-center mb-2'>
                                                    <CFormLabel className='mb-0'>Maintenance Records *</CFormLabel>
                                                    <CButton 
                                                        color='primary' 
                                                        size='sm'
                                                        onClick={addMaintenanceRecord}
                                                        type='button'
                                                    >
                                                        <CIcon icon={cilPlus} /> Add Record
                                                    </CButton>
                                                </div>
                                                
                                                <CTable bordered responsive>
                                                    <CTableHead>
                                                        <CTableRow>
                                                            <CTableHeaderCell>Date *</CTableHeaderCell>
                                                            <CTableHeaderCell>Type of Maintenance *</CTableHeaderCell>
                                                            <CTableHeaderCell>Mileage *</CTableHeaderCell>
                                                            <CTableHeaderCell>Shop Name *</CTableHeaderCell>
                                                            <CTableHeaderCell>Invoice Number</CTableHeaderCell>
                                                            <CTableHeaderCell style={{ width: '50px' }}>Action</CTableHeaderCell>
                                                        </CTableRow>
                                                    </CTableHead>
                                                    <CTableBody>
                                                        {maintenanceRecords.map((record, index) => (
                                                            <CTableRow key={index}>
                                                                <CTableDataCell>
                                                                    <CFormInput
                                                                        type='date'
                                                                        value={record.date}
                                                                        onChange={(e) => updateMaintenanceRecord(index, 'date', e.target.value)}
                                                                        max={new Date().toISOString().split('T')[0]}
                                                                    />
                                                                </CTableDataCell>
                                                                <CTableDataCell>
                                                                    <CFormSelect
                                                                        value={record.type}
                                                                        onChange={(e) => updateMaintenanceRecord(index, 'type', e.target.value)}
                                                                        className='mb-2'
                                                                    >
                                                                        <option value=''>Select type</option>
                                                                        {maintenanceTypes.map(type => (
                                                                            <option key={type} value={type}>{type}</option>
                                                                        ))}
                                                                    </CFormSelect>
                                                                    {record.type === 'Other' && (
                                                                        <CFormInput
                                                                            value={record.typeOther}
                                                                            onChange={(e) => updateMaintenanceRecord(index, 'typeOther', e.target.value)}
                                                                            placeholder='Please specify'
                                                                        />
                                                                    )}
                                                                </CTableDataCell>
                                                                <CTableDataCell>
                                                                    <CFormInput
                                                                        type='number'
                                                                        value={record.mileage}
                                                                        onChange={(e) => updateMaintenanceRecord(index, 'mileage', e.target.value)}
                                                                        placeholder='Miles'
                                                                        min='0'
                                                                    />
                                                                </CTableDataCell>
                                                                <CTableDataCell>
                                                                    <CFormInput
                                                                        value={record.vendor}
                                                                        onChange={(e) => updateMaintenanceRecord(index, 'vendor', e.target.value)}
                                                                        placeholder='Shop Name'
                                                                    />
                                                                </CTableDataCell>
                                                                <CTableDataCell>
                                                                    <CFormInput
                                                                        value={record.invoiceNumber}
                                                                        onChange={(e) => updateMaintenanceRecord(index, 'invoiceNumber', e.target.value)}
                                                                        placeholder='Invoice #'
                                                                    />
                                                                </CTableDataCell>
                                                                <CTableDataCell>
                                                                    <CButton
                                                                        color='danger'
                                                                        size='sm'
                                                                        onClick={() => removeMaintenanceRecord(index)}
                                                                        disabled={maintenanceRecords.length === 1}
                                                                        type='button'
                                                                    >
                                                                        <CIcon icon={cilTrash} />
                                                                    </CButton>
                                                                </CTableDataCell>
                                                            </CTableRow>
                                                        ))}
                                                    </CTableBody>
                                                </CTable>
                                            </CCol>
                                        </CRow>

                                        {/* Greased Question */}
                                        <CRow className='mb-3'>
                                            <CCol>
                                                <CFormLabel className='fw-bold'>
                                                    Did you grease your {vehicleType} this month? *
                                                </CFormLabel>
                                                <div className='d-flex gap-4 mt-2'>
                                                    <CFormCheck
                                                        type='radio'
                                                        name='greased'
                                                        id='greasedYes'
                                                        label='Yes'
                                                        checked={greased === true}
                                                        onChange={() => setGreased(true)}
                                                    />
                                                    <CFormCheck
                                                        type='radio'
                                                        name='greased'
                                                        id='greasedNo'
                                                        label='No'
                                                        checked={greased === false}
                                                        onChange={() => setGreased(false)}
                                                    />
                                                </div>
                                            </CCol>
                                        </CRow>

                                        {/* Oil Change Question */}
                                        <CRow className='mb-3'>
                                            <CCol>
                                                <CFormLabel className='fw-bold'>
                                                    Did you perform an oil change this month? *
                                                </CFormLabel>
                                                <div className='d-flex gap-4 mt-2'>
                                                    <CFormCheck
                                                        type='radio'
                                                        name='oilChange'
                                                        id='oilChangeYes'
                                                        label='Yes'
                                                        checked={oilChange === true}
                                                        onChange={() => setOilChange(true)}
                                                    />
                                                    <CFormCheck
                                                        type='radio'
                                                        name='oilChange'
                                                        id='oilChangeNo'
                                                        label='No'
                                                        checked={oilChange === false}
                                                        onChange={() => setOilChange(false)}
                                                    />
                                                </div>
                                            </CCol>
                                        </CRow>

                                        <CRow className='mb-3'>
                                            <CCol>
                                                <CFormLabel>Upload Invoice(s) (Optional - Max 20 MB per file)</CFormLabel>
                                                <div className='d-flex gap-2 mb-2'>
                                                    <CButton 
                                                        color='secondary' 
                                                        onClick={() => fileInputRef.current?.click()}
                                                        type='button'
                                                    >
                                                        Choose Files
                                                    </CButton>
                                                    <CButton 
                                                        color='info' 
                                                        onClick={startCamera}
                                                        type='button'
                                                    >
                                                        <CIcon icon={cilCamera} /> Take Photos
                                                    </CButton>
                                                </div>
                                                <CFormInput
                                                    ref={fileInputRef}
                                                    type='file'
                                                    id='invoiceFiles'
                                                    multiple
                                                    accept='.pdf,.jpg,.jpeg,.png'
                                                    onChange={handleFileChange}
                                                    style={{ display: 'none' }}
                                                />
                                                {invoiceFiles.length > 0 && (
                                                    <div className='mt-2'>
                                                        <strong>{invoiceFiles.length} file(s) selected:</strong>
                                                        <ul className='mt-2'>
                                                            {invoiceFiles.map((file, index) => (
                                                                <li key={index} className='d-flex justify-content-between align-items-center mb-1'>
                                                                    <span>
                                                                        {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                                                    </span>
                                                                    <CButton 
                                                                        color='danger' 
                                                                        size='sm'
                                                                        onClick={() => removeFile(index)}
                                                                        type='button'
                                                                    >
                                                                        <CIcon icon={cilTrash} />
                                                                    </CButton>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </CCol>
                                        </CRow>
                                    </>
                                )}

                                <CRow className='mb-3'>
                                    <CCol>
                                        <CFormCheck
                                            id='confirmAccuracy'
                                            label='I confirm that all the information provided is correct and accurate *'
                                            checked={confirmAccuracy}
                                            onChange={(e) => setConfirmAccuracy(e.target.checked)}
                                        />
                                    </CCol>
                                </CRow>

                                {errors.length > 0 && (
                                    <CRow className='mb-3'>
                                        <CCol>
                                            {errors.map((error, index) => (
                                                <p key={index} style={{ color: 'red', fontWeight: '600' }}>
                                                    {error}
                                                </p>
                                            ))}
                                        </CCol>
                                    </CRow>
                                )}

                                <CRow>
                                    <CCol>
                                        <CButton 
                                            color='primary' 
                                            type='submit'
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Submitting...' : 'Submit Maintenance Form'}
                                        </CButton>
                                    </CCol>
                                </CRow>
                            </>
                        )}
                    </CForm>
                </CCardBody>
            </CCard>

            {/* Camera Modal */}
            <CModal 
                visible={showCamera} 
                onClose={stopCamera}
                size='xl'
                backdrop='static'
            >
                <CModalHeader closeButton>
                    <CModalTitle>Take Invoice Photos</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <div className='text-center'>
                        <video 
                            ref={videoRef} 
                            autoPlay 
                            playsInline
                            muted
                            style={{ width: '100%', maxHeight: '60vh', backgroundColor: '#000' }}
                        />
                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                        
                        <div className='mt-3'>
                            <CButton 
                                color='primary' 
                                size='lg'
                                onClick={capturePhoto}
                                type='button'
                            >
                                <CIcon icon={cilCamera} /> Capture Photo
                            </CButton>
                        </div>

                        {capturedImages.length > 0 && (
                            <div className='mt-4'>
                                <h5>Captured Photos ({capturedImages.length})</h5>
                                <div className='d-flex flex-wrap gap-2 justify-content-center'>
                                    {capturedImages.map((img, index) => (
                                        <div key={index} style={{ position: 'relative' }}>
                                            <img 
                                                src={img.preview} 
                                                alt={`Captured ${index + 1}`}
                                                style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                                            />
                                            <CButton
                                                color='danger'
                                                size='sm'
                                                style={{ 
                                                    position: 'absolute', 
                                                    top: '5px', 
                                                    right: '5px' 
                                                }}
                                                onClick={() => removeCapturedImage(index)}
                                                type='button'
                                            >
                                                <CIcon icon={cilTrash} />
                                            </CButton>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </CModalBody>
                <CModalFooter>
                    <CButton color='secondary' onClick={stopCamera} type='button'>
                        Cancel
                    </CButton>
                    <CButton 
                        color='success' 
                        onClick={savePhotos}
                        disabled={capturedImages.length === 0}
                        type='button'
                    >
                        Save {capturedImages.length} Photo(s)
                    </CButton>
                </CModalFooter>
            </CModal>
        </>
    );
}